import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Page from "../molecules/Page";
import { GoogleLogin } from '@react-oauth/google';
import posthog from 'posthog-js';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from "@capgo/capacitor-social-login";
import * as Sentry from '@sentry/react';
import { handleAuthError, reportMissingToken } from '../../utils/authErrorHelper';
import { invalidateApiCache } from '../../utils/apiCache';
import { trackRegistration } from '../../api/tiktokEvents';
import googleIcon from '../../img/icons/google.svg';
import appleIcon from '../../img/icons/apple.svg';

axios.defaults.withCredentials = true;

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const csrftoken = getCookie('csrftoken');
if (csrftoken) {
  axios.defaults.headers.common['X-CSRFToken'] = csrftoken;
}

const apiUrl = process.env.REACT_APP_API_SOURCE;

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [socialLoginFailed, setSocialLoginFailed] = useState(false);
  const [authLoading, setAuthLoading] = useState(null);
  const navigate = useNavigate();
  const [platform, setPlatform] = useState('web');

  useEffect(() => {
    const currentPlatform = Capacitor.getPlatform();
    setPlatform(currentPlatform);

    if (currentPlatform !== 'web') {
      const initializeCapacitorPlugin = async () => {
        try {
          if (currentPlatform === 'ios') {
            await SocialLogin.initialize({
              apple: {
                clientId: 'matematly',
              },
            });
          }
          if (currentPlatform === 'android') {
            await SocialLogin.initialize({
              google: {
                webClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID
              },
            });
          }
        } catch (e) {
          Sentry.captureException(e, {
            tags: { auth_method: 'social_plugin', auth_step: 'plugin_init', platform: currentPlatform },
            extra: { page: 'Login', platform: currentPlatform },
          });
          console.error("Failed to initialize Capacitor Social Login", e);
        }
      };
      initializeCapacitorPlugin();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (authLoading) return; // blokujemy klikanie na czas ładowania

    setSocialLoginFailed(false);
    setAuthLoading('email');
    
    try {
      const response = await axios.post(apiUrl + "login/", { email, password });
      
      if (response.status === 200) {
        const { token, preferences } = response.data;
        localStorage.setItem("token", token);
        localStorage.setItem("user_id", preferences.user_id);
        localStorage.setItem("user_preferences", JSON.stringify(preferences));
        invalidateApiCache("user_dashboard_data", true);

        if (preferences && preferences.user_id) {
            posthog.identify(preferences.user_id);
            posthog.people.set({
                platform: Capacitor.getPlatform(),
                is_mobile_app: Capacitor.isNativePlatform(),
                user_id: preferences.user_id
            });
        }
        
        // --- KLUCZOWY FIX ---
        // Zdejmujemy focus z inputów, zmuszając Androida do schowania klawiatury
        // przed startem animacji wyjścia Framer Motion.
        if (document.activeElement) {
          document.activeElement.blur();
        }
        // Przenosimy. Spinner naturalnie wyjeżdża razem ze stroną logowania.
        navigate("/app");
        // -------------------
      }
    } catch (error) {
      setAuthLoading(null);
      const msg = handleAuthError(error, {
        method: 'email',
        step: 'backend_request',
        fallbackMessage: 'Logowanie nie powiodło się. Spróbuj ponownie.',
      });
      if (msg) {
        setError(msg);
      }
    }
  };

  const handleSocialLoginSuccess = (response) => {
    const { token, preferences } = response.data;
    localStorage.setItem("token", token);
    localStorage.setItem("user_id", preferences.user_id);
    localStorage.setItem("user_preferences", JSON.stringify(preferences));
    invalidateApiCache("user_dashboard_data", true);

    if (preferences && preferences.user_id) {
        posthog.identify(preferences.user_id);
        posthog.people.set({
            platform: Capacitor.getPlatform(),
            is_mobile_app: Capacitor.isNativePlatform(),
            user_id: preferences.user_id
        });

        // Google/Apple zakladaja konto takze z ekranu logowania — bez tego rejestracje
        // z tej sciezki nie trafialy do TikToka. Bierzemy wylacznie jawna flage z
        // backendu: tu, w odroznieniu od ekranu rejestracji, ciagle wchodza wracajacy
        // uzytkownicy, a niedokonczony profil nie znaczy u nich "swieze konto".
        if (response.data.is_new_account === true) {
            trackRegistration(preferences.user_id);
        }
    }
    
    // Zdejmujemy focus profilaktycznie
    if (document.activeElement) {
      document.activeElement.blur();
    }
    navigate("/app");
  };

  const handleWebGoogleLoginSuccess = async (credentialResponse) => {
    setError(null);
    setSocialLoginFailed(false);
    setAuthLoading('google');
    try {
      const response = await axios.post(`${apiUrl}google-login/`, {
        token: credentialResponse.credential,
        platform: Capacitor.getPlatform(),
      });
      if (response.status === 200) {
        handleSocialLoginSuccess(response);
      }
    } catch (error) {
      setAuthLoading(null);
      const msg = handleAuthError(error, {
        method: 'google_web',
        step: 'backend_request',
        fallbackMessage: 'Logowanie przez Google nie powiodło się. Spróbuj ponownie.',
      });
      if (msg) {
        setError(msg);
        setSocialLoginFailed(true);
      }
    }
  };

  const handleWebGoogleLoginError = () => {
    setAuthLoading(null);
    Sentry.captureMessage('Google Login widget error (Login)', {
      level: 'warning',
      tags: { auth_method: 'google_web', auth_step: 'widget_error', platform: 'web' },
    });
    setError("Wystąpił błąd podczas logowania przez Google.");
    setSocialLoginFailed(true);
  };

  const handleNativeGoogleLogin = async () => {
    if (authLoading) return;
    setError(null);
    setSocialLoginFailed(false);
    setAuthLoading('google');

    let result;
    try {
      result = await SocialLogin.login({ provider: 'google', options: { scopes: ['email', 'profile'] } });
    } catch (err) {
      setAuthLoading(null);
      const msg = handleAuthError(err, {
        method: 'google_native',
        step: 'plugin_login',
        fallbackMessage: 'Logowanie Google nie powiodło się. Spróbuj ponownie.',
      });
      if (msg) {
        setError(msg);
        setSocialLoginFailed(true);
      }
      return;
    }

    if (!result?.result?.idToken) {
      setAuthLoading(null);
      const isRealProblem = reportMissingToken('google_native', result);
      if (isRealProblem) {
        setError("Nie uzyskano danych z Google. Spróbuj ponownie.");
        setSocialLoginFailed(true);
      }
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}google-login/`, {
        token: result.result.idToken,
        platform: Capacitor.getPlatform(),
      });
      if (response.status === 200) {
        handleSocialLoginSuccess(response);
      }
    } catch (err) {
      setAuthLoading(null);
      const msg = handleAuthError(err, {
        method: 'google_native',
        step: 'backend_request',
        fallbackMessage: 'Nie udało się dokończyć logowania Google. Spróbuj ponownie.',
      });
      if (msg) {
        setError(msg);
        setSocialLoginFailed(true);
      }
    }
  };

  const handleWebAppleSignIn = () => {
    setAuthLoading('apple');
    const client_id = 'matematly';
    const redirect_uri = process.env.REACT_APP_APPLE_REDIRECT_URI;
    const state = Math.random().toString(36).substring(2, 15);
    window.location.href = `https://appleid.apple.com/auth/authorize?client_id=${client_id}&redirect_uri=${encodeURIComponent(redirect_uri)}&response_type=code%20id_token&scope=name%20email&response_mode=form_post&state=${state}`;
  };
  
  const handleNativeAppleLogin = async () => {
    if (authLoading) return;
    setError(null);
    setSocialLoginFailed(false);
    setAuthLoading('apple');

    let result;
    try {
      result = await SocialLogin.login({ provider: 'apple', options: { scopes: ['email', 'name'] } });
    } catch (err) {
      setAuthLoading(null);
      const msg = handleAuthError(err, {
        method: 'apple_native',
        step: 'plugin_login',
        fallbackMessage: 'Logowanie Apple nie powiodło się. Spróbuj ponownie.',
      });
      if (msg) {
        setError(msg);
        setSocialLoginFailed(true);
      }
      return;
    }

    if (!result?.result?.idToken) {
      setAuthLoading(null);
      const isRealProblem = reportMissingToken('apple_native', result);
      if (isRealProblem) {
        setError("Nie uzyskano danych z Apple. Spróbuj ponownie.");
        setSocialLoginFailed(true);
      }
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}mobile-apple-login/`, {
        token: result.result.idToken,
        platform: Capacitor.getPlatform(),
      });
      if (response.status === 200) {
        handleSocialLoginSuccess(response);
      }
    } catch (err) {
      setAuthLoading(null);
      const msg = handleAuthError(err, {
        method: 'apple_native',
        step: 'backend_request',
        fallbackMessage: 'Nie udało się dokończyć logowania Apple. Spróbuj ponownie.',
      });
      if (msg) {
        setError(msg);
        setSocialLoginFailed(true);
      }
    }
  };

  return (
<Page showNav={false} disableExit={true}>
        <div className="bg-white lg:bg-transparent w-full">
      <div className="flex flex-col max-w-2xl mx-auto w-full px-4 lg:px-0">
        <div className="w-full lg:px-14 py-8 lg:py-10 lg:mt-[8vh] lg:bg-white lg:rounded-[3rem] lg:shadow-[4px_4px_12px_rgba(0,0,0,0.30)] lg:border lg:border-gray-200 space-y-6">

          <h2 className="text-center text-xl font-apotek uppercase tracking-widest font-semibold text-[#233855]">
            Logowanie
          </h2>

          <div className="flex flex-col gap-3 w-full">
            {(platform === 'ios' || platform === 'web') && (
              <button
                type="button"
                onClick={platform === 'ios' ? handleNativeAppleLogin : handleWebAppleSignIn}
                className={`w-full px-4 py-2.5 border border-gray-300 flex justify-center items-center gap-2 rounded-lg text-gray-700 transition-all duration-150 ${authLoading === 'apple' ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'hover:border-gray-400 hover:text-gray-900 hover:shadow-sm active:scale-[0.98]'}`}
              >
                {authLoading === 'apple' ? (
                  <span className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                ) : (
                  <img className="w-5 h-5" src={appleIcon} alt="apple logo" />
                )}
                <span>{authLoading === 'apple' ? "Logowanie…" : "Zaloguj się przez Apple"}</span>
              </button>
            )}
            {platform !== 'web' ? (
              <button
                type="button"
                onClick={handleNativeGoogleLogin}
                className={`w-full px-4 py-2.5 border border-gray-300 flex justify-center items-center gap-2 rounded-lg text-gray-700 transition-all duration-150 ${authLoading === 'google' ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'hover:border-gray-400 hover:text-gray-900 hover:shadow-sm active:scale-[0.98]'}`}
              >
                {authLoading === 'google' ? (
                  <span className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                ) : (
                  <img className="w-5 h-5" src={googleIcon} alt="google logo" />
                )}
                <span>{authLoading === 'google' ? "Logowanie…" : "Zaloguj się przez Google"}</span>
              </button>
            ) : (
              <div style={{ width: 'fit-content', margin: '0 auto' }} className={authLoading ? 'opacity-60 pointer-events-none' : ''}>
                <GoogleLogin
                  onSuccess={handleWebGoogleLoginSuccess}
                  onError={handleWebGoogleLoginError}
                  useOneTap
                  size="large"
                />
              </div>
            )}
          </div>

          {socialLoginFailed && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
              <p className="text-sm text-amber-800">
                {error}
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Możesz też zalogować się za pomocą adresu email i hasła poniżej.
              </p>
            </div>
          )}
          
          <div className="relative flex py-2 items-center">
            <div className="flex-grow border-t border-gray-200"></div>
            <span className="flex-shrink mx-4 text-gray-400 text-xs uppercase tracking-widest">Lub</span>
            <div className="flex-grow border-t border-gray-200"></div>
          </div>

          <form className="space-y-6" onSubmit={handleLogin}>
            <div>
              <label htmlFor="email" className="block text-[0.8rem] font-apotek uppercase tracking-widest font-semibold text-[#233855] mb-2">
                Adres email
              </label>
              <input
                type="text"
                name="email"
                id="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-base text-gray-900 placeholder:text-gray-400 focus:border-[#233855] focus:ring-1 focus:ring-[#233855] sm:text-sm/6"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-[0.8rem] font-apotek uppercase tracking-widest font-semibold text-[#233855]">
                  Hasło
                </label>
                <div className="text-xs">
                  <a href="/password_reset" className="font-semibold text-[#8A0000] hover:text-[#345081]">
                    Zapomniałeś hasła?
                  </a>
                </div>
              </div>
              
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  id="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-lg border border-gray-200 bg-white px-3 pr-10 py-2 text-base text-gray-900 placeholder:text-gray-400 focus:border-[#233855] focus:ring-1 focus:ring-[#233855] sm:text-sm/6"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-800"
                  aria-label={showPassword ? "Ukryj hasło" : "Pokaż hasło"}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.243 4.243L6.228 6.228" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && !socialLoginFailed && (
              <p className="text-xs text-center text-red-600">{error}</p>
            )}

            <div>
              <button
                type="submit"
                className={`flex w-full justify-center items-center gap-2 rounded-lg bg-[#233855] px-3 py-2.5 text-sm font-apotek uppercase tracking-widest text-white transition-all ${authLoading === 'email' ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'shadow-lg hover:bg-[#345081] active:scale-[0.98]'}`}
              >
                {authLoading === 'email' && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                {authLoading === 'email' ? "Logowanie…" : "Zaloguj się"}
              </button>
            </div>
            <div className="flex w-full justify-center">
              <div className="text-sm text-gray-600">
                Nie masz konta?{" "}
                <a href="/register" className="font-semibold text-[#233855] hover:text-[#345081]">
                  Zarejestruj się
                </a>
              </div>
            </div>
          </form>
        </div>
      </div>
      </div>
    </Page>
  );
};

export default Login;